import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';

export const Index_SettingOfCareFragment = gql(/* GraphQL */ `
  fragment Index_SettingOfCareFragment on HealthcareIndicators {
    settingOfCare {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type SettingOfCareProps = {
  className?: string;
  indicators: FragmentType<typeof Index_SettingOfCareFragment>;
};

export function SettingOfCare({ indicators, className }: SettingOfCareProps) {
  const { settingOfCare } = useFragment(Index_SettingOfCareFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('settingOfCare');

  const pieData = useMemo(() => {
    const colors = generateChartColors(settingOfCare.items.length);
    const sorted = orderBy(
      settingOfCare.items
        .filter((item) => !!item)
        .map((item) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [settingOfCare.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip>{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={settingOfCare.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="right" />
      </ChartCard.Body>
    </ChartCard>
  );
}
